import { ref } from 'vue';

import { storeToRefs } from 'pinia';

import { useLanguage } from '@/composables/shared/useLanguage';
import { usePreferences } from '@/composables/shared/usePreferences';
import { useSocketStore } from '@/stores/socket';
import { LanguageCode } from '@/types/common';
import { UseHandlerMessagesSubtitleOptions } from '@/types/composables';
import { ChatMessage, IMessageFc, MessageCa, MessageCd, MessageHistory, MessageText, MessageTr } from '@/types/socket';
import { InboundMessageDM } from '@/types/socket/inbound';
import { isLanguageNoSpace } from '@/utils/chat/language';
import { SocketState } from '@/utils/enums';

export function useMessagesSubtitle({ messageList, prevMyMessageId }: UseHandlerMessagesSubtitleOptions) {
  const socketStore = useSocketStore();

  const { socket } = storeToRefs(socketStore);
  const { changeLanguage } = useLanguage();
  const { updateRemotePreferences } = usePreferences();

  const messageMap = ref({}); // 정제된 새로운 메세지

  // 1. 웹 소켓 메세지 처리
  const handleMessageWebSocket = ({ data }) => {
    const flag = data.slice(0, 2);

    switch (flag) {
      case 'pi':
        return;
      case 'td':
        socketStore.recover = true;
        return;
      case 'nd':
        socket.value.close();
        localStorage.setItem('accessAuthentication', '0');
        return;
    }

    // Ensure message is parsed properly
    const message = parseWebSocketMessage(data);
    if (!message) return;

    switch (flag) {
      case 'cc':
        return;
      case 'cd':
        return handleMessageCd(message);
      case 'ca':
        return handleMessageCa(message);
      case 'at':
        return;
      case 'al':
        return;
      case 'am':
        return;
      case 'fi':
        return;
      case 'fc':
        return handleMessageFc(message);
      case 'ts':
        handleMessageTs(message);
        break;
      case 'tr':
        handleMessageTr(message);
        break;
      case 'dm':
        handleMessageDm(message);
        break;
      case 'er':
        if (message.code === '2002') {
          console.error('No audio header!!');
          socketStore.socketState = SocketState.ERROR_NO_HEADER;
        }
        return;
      default:
        return;
    }
  };

  // 2. 웹소켓 메시지 정제
  const parseWebSocketMessage = (data) => {
    try {
      if (data instanceof ArrayBuffer) {
        data = new TextDecoder().decode(data);
      }
      if (typeof data !== 'string') return null;

      const length = parseInt(data.slice(2, 10), 10);
      if (data.length < 10 + length) throw new Error('Data length mismatch');

      return JSON.parse(data.substring(10));
    } catch (error) {
      console.error('Failed to parse WebSocket message:', error);
      return null;
    }
  };

  const handleMessageCa = (message: MessageCa) => {
    localStorage.setItem('userIdConnected', localStorage.getItem('userId') ?? '');
    socketStore.selectedChatId = message.chatId;
    socketStore.connectionResponse = 2;
    socketStore.socketState = SocketState.OPEN;
  };

  // 연결 거부
  const handleMessageCd = (message: MessageCd) => {
    if (message.reasonCode === '0') return false;
    if (message.userId !== sessionStorage.getItem('userId')) return true;
    if (message.reasonCode === '2') {
      localStorage.setItem('accessAuthentication', '0');
      socketStore.evicted = true;
      socket.value?.close();

      return true;
    }
  };

  // 입력중
  const handleMessageTs = (message) => {
    prevMyMessageId.value = message.callLogId;
  };

  // 통역 업데이트
  const handleMessageTr = async (messageTr: MessageTr) => {
    const {
      chatId,
      callLogId,
      isAudioComplete,
      dataFlag,
      isTextComplete,
      message,
      sentAt,
      senderId,
      reset,
      chatSeqSubId,
      logFlag,
      isComplete,
      isFastInterpretationOn,
      languageCode,
      voiceKey,
      incompleteText,
    } = messageTr;

    if (dataFlag === 'src') return;
    const indexMessage = messageList.value.findIndex((msg) => msg.callLogId === callLogId);

    if (isAudioComplete && !messageMap.value[callLogId]) {
      console.debug('오디오 파일만 있는 빈 버블 현상을 방지했습니다');
      return; // 글자 없이 오디오 파일만 받은 경우
    }

    if (indexMessage !== -1) {
      messageList.value[indexMessage].isAudioComplete = isAudioComplete;
      // 오디오 수신 완료 시 showLoading 강제 False 처리
      if (isAudioComplete && messageList.value[indexMessage].showLoading) {
        messageList.value[indexMessage].showLoading = false;
        messageList.value[indexMessage].reset = '1';
      }
    }

    const nicknameDecoded = socketStore.findAttendeeNickname(senderId!);

    initializeMessageMap(callLogId, nicknameDecoded, senderId ?? '', sentAt, reset);

    // 이미 완료된 메세지는 더 어상 수정하지 않음
    if (
      messageMap.value[callLogId]?.[`${dataFlag}History`]?.[chatSeqSubId]?.thoroughlyCompletedContent?.length > 0 &&
      !voiceKey
    ) {
      console.error('앞의 내용이 누락되지 않고 이 메세지가 없다면 해결 완료:', message);
      return;
    }

    if (!isAudioComplete) {
      console.log(
        `[TEXT][${dataFlag}] id: ${callLogId} ${chatSeqSubId} ${logFlag}, text/isComplete: ${isTextComplete} ${isComplete} (${isFastInterpretationOn}) ${senderId}, timestamp : ${new Date().toLocaleString()}, msg: ${message}, inCompleteText: ${incompleteText}`
      );
    } else if (isAudioComplete) {
      console.log(
        `[AUDIO] ${dataFlag} callLogId: ${callLogId}, subId: ${chatSeqSubId}, completed:${isTextComplete}/${isAudioComplete}, msg: ${message}, vUrl: ${voiceKey}`
      );
    }

    if (sentAt <= 0) return;

    if (!['tgt'].includes(dataFlag)) return;

    const historyKey = `${dataFlag}History`;
    const languageSetKey = `${dataFlag}LanguageSet`;

    messageMap.value[callLogId][dataFlag] ??= {};
    messageMap.value[callLogId][historyKey] ??= {};
    messageMap.value[callLogId][languageSetKey] ??= new Set();
    messageMap.value[callLogId][languageSetKey].add(languageCode);
    messageMap.value[callLogId][dataFlag].isComplete = isComplete;
    messageMap.value[callLogId][dataFlag].isAudioComplete = isAudioComplete;
    messageMap.value[callLogId].nickname = nicknameDecoded;

    if (isAudioComplete) {
      messageMap.value[callLogId][dataFlag] ??= {};
      messageMap.value[callLogId].isAudioComplete = true;
      const existingAudioKeys = messageMap.value[callLogId][dataFlag]?.audioKey ?? [];
      const newAudioEntry = voiceKey
        ? {
            chatId: chatId,
            chatSeqSubId: chatSeqSubId,
            voiceKey: voiceKey,
            callLogId: callLogId,
            logFlag: logFlag,
          }
        : null;

      messageMap.value[callLogId][dataFlag].audioKey ??= existingAudioKeys;
      if (newAudioEntry) {
        messageMap.value[callLogId][dataFlag].audioKey.push(newAudioEntry);
      }

      updateMessageList(callLogId, 'tr');

      return;
    } else messageMap.value[callLogId].isAudioComplete = false;

    // 동시통역
    switch (isFastInterpretationOn) {
      case '1': // 켜짐
        handleMessageFiOn(messageTr, historyKey);
        break;
      case '0': // 꺼짐
        handleMessageFiOff(messageTr, historyKey);
        break;
    }

    updateMessageList(callLogId, 'tr');
  };

  // 빠른 통역
  const handleMessageFiOn = (messageTr: MessageTr, historyKey: string) => {
    const {
      callLogId,
      chatSeqSubId,
      message,
      incompleteText,
      isTextComplete,
      isComplete,
      logFlag,
      dataFlag,
      languageCode,
    } = messageTr;

    const history = (messageMap.value[callLogId][historyKey][chatSeqSubId] ??= {
      completedContents: [],
      creatingContent: null,
    });
    const newMessage = { message, logFlag };

    if (isTextComplete || isComplete) {
      if (isComplete && message.trim() === '' && !history.creatingContent) {
        return;
      }

      if (isComplete) {
        history.thoroughlyCompletedContent = [...history.completedContents, newMessage];
        history.key = Date.now();
        history.completedContents = [];
        history.creatingContent = null;
      } else {
        history.completedContents.push(newMessage);
      }

      history.creatingContent = {
        message: incompleteText,
        logFlag,
      };
    } else {
      if (!history.thoroughlyCompletedContent) {
        history.creatingContent = newMessage;
      }
    }

    messageMap.value[callLogId][dataFlag] ??= {};
    updateContentsFromHistory(callLogId, dataFlag, logFlag, languageCode);
  };

  // 확정 통역
  const handleMessageFiOff = (messageTr: MessageTr, historyKey: string) => {
    const { callLogId, chatSeqSubId, message, isComplete = false, dataFlag, logFlag, languageCode } = messageTr;

    const history = (messageMap.value[callLogId][historyKey][chatSeqSubId] ??= {
      completedContents: [],
      content: '',
      isTextCompleted: false,
      key: Date.now(),
    });

    if (isComplete) {
      history.completedContents = [...history.completedContents, { message, logFlag }];
      history.creatingContent = null;
    } else {
      history.creatingContent = { message, logFlag };
    }

    messageMap.value[callLogId][dataFlag] ??= {};
    updateContentsFromHistory(callLogId, dataFlag, logFlag, languageCode);
  };

  // 메세지 맵 초기화
  const initializeMessageMap = (
    callLogId: string,
    nickname: string,
    senderId: string,
    sentAt: number,
    reset: '0' | '1'
  ) => {
    if (!messageMap.value[callLogId]) {
      messageMap.value[callLogId] = {
        src: { contents: [], isComplete: false },
        srcHistory: {},
        srcLanguageSet: new Set(),
        tgt: { contents: [], isComplete: false },
        tgtHistory: {},
        tgtLanguageSet: new Set(),
        sentAt: sentAt ?? new Date(),
        senderId: senderId,
        nickname: nickname,
        reset: reset,
        timer: 0,
      };
    }
  };

  // 축적된 history를 메세지에 담기
  const updateContentsFromHistory = (
    callLogId: string,
    dataFlag: string,
    logFlag: string,
    languageCode: LanguageCode
  ) => {
    const historyKey = `${dataFlag}History`;
    messageMap.value[callLogId][dataFlag].contents = Object.entries(
      messageMap.value[callLogId][historyKey] as Record<string, MessageHistory>
    )
      .flatMap(([key, value], index, array) => {
        const isLast = index === array.length - 1;
        const isFirst = index === 0;
        const result: Array<MessageText> = [];

        const isNoSpaceLang = isLanguageNoSpace(languageCode);
        const splitter = isNoSpaceLang ? '' : ' ';

        if (value.thoroughlyCompletedContent) {
          let contentParsed = value.thoroughlyCompletedContent.map((content) => content.message).join(splitter);
          if (isNoSpaceLang) contentParsed = contentParsed.replaceAll(' ', '');
          if (!isFirst) contentParsed = splitter + contentParsed;

          result.push({
            highlight: false,
            content: contentParsed,
            fadeout: true,
            key: value.key,
            isThoroughlyComplete: true,
            messageEdited: value.messageEdited,
            chatSeqSubId: key,
            isEditable: true,
            logFlag,
            languageCode,
          });
        } else if (value.completedContents?.length > 0) {
          let contentParsed = value.completedContents.map((content) => content.message).join(splitter);
          if (!isFirst) contentParsed = splitter + contentParsed;

          result.push({
            highlight: false,
            content: contentParsed,
            fadeout: isLast,
            key: Date.now(),
            isThoroughlyComplete: false,
            messageEdited: value.messageEdited,
            chatSeqSubId: key,
            // isEditable: true,
            logFlag,
            languageCode,
          });
        }

        if (value.creatingContent?.message?.trim?.()) {
          result.push({
            highlight: true,
            content: `${splitter}${value.creatingContent.message}`,
            fadeout: false,
            key: Date.now(),
            isThoroughlyComplete: false,
            messageEdited: value.messageEdited,
            chatSeqSubId: key,
            logFlag,
            languageCode,
          });
        }

        return result;
      })
      .sort((a, b) => parseInt(a.chatSeqSubId ?? '0') - parseInt(b.chatSeqSubId ?? '0'));
  };

  // 메세지 배열 갱신
  const updateMessageList = (messageId: string, flag): void => {
    if (!messageMap.value[messageId]) return;
    const data = messageMap.value[messageId];

    const { src, tgt } = getMessageContents(data);

    const message: ChatMessage = {
      callLogId: messageId,
      src,
      tgt,
      srcAudioKeys: data.src?.audioKey ?? [],
      tgtAudioKeys: data.tgt?.audioKey ?? [],
      sentAt: data.sentAt,
      inout: data.inout,
      isOwn: data.senderId === localStorage.getItem('userIdConnected'),
      time: new Date(data.sentAt).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      }),
      senderId: (data.senderId || data.userId) as string,
      nickname: data.nickname,
      autoPlay: true,
      flag: flag,
      reset: data.reset as '0' | '1',
      srcLanguageSet: data.srcLanguageSet!,
      tgtLanguageSet: data.tgtLanguageSet!,
      isAudioComplete: data.isAudioComplete ?? false,
    };

    const index = messageList.value.findIndex((msg) => msg.callLogId === messageId);

    if (index !== -1) {
      messageList.value[index] = { ...messageList.value[index], ...message };
    } else {
      messageList.value.push(message);
    }

    messageList.value = [...messageList.value]; // force reactivity
  };

  const getMessageContents = (data) => {
    const messageContents = { src: {}, tgt: {} };

    if (data.src) {
      const { contents = [], isComplete = false, isAudioComplete = false } = data.src;
      messageContents.src = {
        contents: contents.filter(({ content = '' }) => content),
        isComplete,
        isAudioComplete,
      };
    }

    if (data.tgt) {
      const { contents = [], isComplete = false, isAudioComplete = false } = data.tgt;
      messageContents.tgt = {
        contents: contents.filter(({ content = '' }) => content),
        isComplete,
        isAudioComplete,
      };
    }

    return messageContents;
  };

  // 사용자 설정값 강제변경
  const handleMessageFc = (messageFc: IMessageFc) => {
    if (!messageFc.subtitleAttribute) return;

    const subtitleSettings = messageFc.subtitleAttribute;
    const langaugeCode = subtitleSettings.languageCode;

    changeLanguage(langaugeCode);
    updateRemotePreferences(subtitleSettings);
  };

  const handleMessageDm = (msg: InboundMessageDM) => {
    const inx = messageList.value.findIndex(({ callLogId }) => callLogId === msg.chatLogId);
    if (inx !== -1) {
      messageList.value.splice(inx, 1);
    }
  };

  return {
    messageMap,
    handleMessageWebSocket,
    handleMessageCd,
    handleMessageTs,
  };
}
